import React, { ReactElement, useCallback, useEffect, useState } from "react";

import { Checkbox } from "@cofano/cck";

import { Box, Typography } from "@mui/material";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineItem from "@mui/lab/TimelineItem";
import Timeline from "@mui/lab/Timeline";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineConnector from "@mui/lab/TimelineConnector";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocation, faRoute } from "@fortawesome/pro-solid-svg-icons";

import DataFormat from "components/utils/DataFormat";
import { request } from "components/utils/Request2";

import { FormattedDate } from "utils/DateUtils";

import { Language } from "imports/Utils";

import { getHistoryNodes, HistoryItem, HistoryNodeItem } from "../../utils/MapUtils";

type HistoryTimelineProps = {
	history?: HistoryItem;
	onTimelineUpdate?: (
		selectedNode?: HistoryNodeItem,
		geofences?: string[],
		isTimelineAvailable?: boolean
	) => void;
	onSelect: (node: HistoryNodeItem | undefined) => void;
}

const MapShipHistoryTimeLine = ({ history, onTimelineUpdate, onSelect }: HistoryTimelineProps) => {

	const containerRef = React.useRef<HTMLDivElement>(null);

	const [ timelineItems, setTimelineItems ] = useState<ReactElement[]>([]);
	const [ geofences, setGeofences ] = useState<string[]>([]);
	const [ selectedNode, setSelectedNode ] = useState<HistoryNodeItem | undefined>(undefined);

	const handleNodeClick = (node: HistoryNodeItem) => {
		setSelectedNode((node.id === selectedNode?.id) ? undefined : node);
		onSelect(node);
	};

	const getLocationByReverseGeocode = useCallback(async (position: {
		latitude?: number | null;
		longitude?: number | null;
		polyline?: string | null;
		description?: string | null;
	} | null | undefined) => {
		const response: { location: { street?: string; city?: string } } = await request({
			url: `geocode/reverse?lat=${position?.latitude}&lng=${position?.longitude}`,
		});
		try {
			if (!response.location.street && !response.location.city) {
				return false;
			}
			let returnString = `${response.location.street || ""} `;
			if (response.location.street && response.location.city) {
				returnString += `(${response.location.city})`;
			} else if (!response.location.street && response.location.city) {
				returnString += `${response.location.city}`;
			}
			return returnString;
		} catch (e) {
			return false;
		}
	}, []);

	const toggleGeofence = (checked: boolean, node: HistoryNodeItem) => {
		if (!node.id) {
			return;
		}

		setGeofences(prevGeofences => {
			if (checked) {
				if (!prevGeofences.includes(node.id!)) {
					return [ ...prevGeofences, node.id! ];
				}
				return prevGeofences;
			} else {
				return prevGeofences.filter(id => id !== node.id);
			}
		});
	};

	const createTimelineItems = useCallback(async () => {
		const items: ReactElement[] = [];
		const nodes = history ? getHistoryNodes(history) : [];

		for (let i = 0; i < nodes.length; i++) {
			const node = nodes[i];
			const isActive = node.id === selectedNode?.id;
			const showConnectorAfter = i === nodes.length - 1;

			if (node.type === "STOP" && node.location?.latitude && node.location?.longitude) {
				items.push(
					<Timeline position="alternate"
						sx={{
							mt: -2,
							mb: -2,
						}}
						key={node.id + "-stop-timeline"}>
						<TimelineItem key={node.id + "-stop"} sx={{ minHeight: "60px" }}>
							<TimelineOppositeContent sx={styles.timelineOppositeContentStop}>
								<Typography variant="caption" color="text.secondary" sx={styles.captionLineHeight12}>
									{node.location?.description || await getLocationByReverseGeocode(node.location) || Language("UNKNOWN")}
								</Typography>
							</TimelineOppositeContent>

							<TimelineSeparator>
								<TimelineDot sx={styles.timelineDot(isActive)}>
									<FontAwesomeIcon
										icon={faLocation}
										color={isActive ? "white" : undefined} />
								</TimelineDot>
								{!showConnectorAfter && <TimelineConnector />}
							</TimelineSeparator>

							<Box
								onClick={(e) => {
									if ((e.target as HTMLElement).closest(".interactive")) {
										return;
									}
									if ((e.target as HTMLElement).closest(".toggle-geofence")) {
										return;
									}
									handleNodeClick(node);
								}}
								sx={styles.clickableBox(isActive)}>
								<TimelineContent sx={{ py: "4px", px: 1, display: "flex", flexDirection: "column" }} >
									<Typography variant="caption" color="text.secondary">
										<FormattedDate date={node.startTime} />
									</Typography>

									<Typography variant="caption" color="text.secondary">
										<FormattedDate date={node.endTime} />
									</Typography>

									{(node.location?.encodedPolyline) && (
										<Box className="toggle-geofence" onClick={(e) => e.stopPropagation()} sx={styles.geofenceToggle}>
											<Typography variant="caption" color="text.secondary">
												<Checkbox
													checked={node.id !== undefined && geofences.includes(node.id)}
													onChange={(event) => toggleGeofence(event.target.checked, node)}
													size="small" />
											</Typography>

											<Typography style={{ marginLeft: 25, marginTop: 2 }} variant="caption" color="text.secondary">
												{Language("SHOW_GEOFENCES")}
											</Typography>
										</Box>
									)}
								</TimelineContent>
							</Box>
						</TimelineItem>
					</Timeline>,
				);
			} else {
				items.push(
					<Timeline position="alternate"
						sx={{
							mt: -2,
							mb: -2,
						}}
						key={node.id + "-route-timeline"}>
						<TimelineItem key={node.id + "-route"} sx={{ minHeight: "60px" }}>
							<TimelineOppositeContent sx={styles.timelineOppositeContentRoute}>
								<Typography variant="caption" color="text.secondary" sx={styles.captionLineHeight1}>
									{DataFormat.distance(node.distanceTravelled)}
								</Typography>
							</TimelineOppositeContent>

							<TimelineSeparator>
								<TimelineDot sx={styles.timelineDot(isActive)}>
									<FontAwesomeIcon
										icon={faRoute}
										color={isActive ? "white" : undefined} />
								</TimelineDot>
								<TimelineConnector />
							</TimelineSeparator>

							<Box
								onClick={() => handleNodeClick(node)}
								sx={styles.clickableBox(isActive)}>
								<TimelineContent sx={{ py: "4px", px: 1, display: "flex", flexDirection: "column"  }}>
									<Typography variant="caption" color="text.secondary">
										<FormattedDate date={node.startTime} />
									</Typography>
									<Typography variant="caption" color="text.secondary">
										<FormattedDate date={node.endTime} />
									</Typography>
								</TimelineContent>
							</Box>
						</TimelineItem>
					</Timeline>,
				);
			}
		}
		setTimelineItems(items);
	}, [ selectedNode, history, geofences, getLocationByReverseGeocode ]);

	useEffect(() => {
		if (onTimelineUpdate) {
			const isTimelineAvailable = timelineItems.length > 0;
			onTimelineUpdate(selectedNode, geofences, isTimelineAvailable);
		}
	}, [ selectedNode, geofences, timelineItems, onTimelineUpdate ]);

	useEffect(() => {
		if (history) {
			createTimelineItems();
			return;
		}
		setTimelineItems([]);
	}, [ selectedNode, history, geofences, getLocationByReverseGeocode ]);

	useEffect(() => {
		setSelectedNode(undefined);
		setGeofences([]);
	}, [ history ]);


	if (timelineItems.length > 0) {
		return (
			<Box sx={{ width: "100%", maxWidth: 600, mx: "auto", p: 2 }} ref={containerRef}>
				{timelineItems}
			</Box>
		);
	} else {
		return <React.Fragment />;
	}
};

const styles = {
	geofenceToggle: {
		mt: 1,
		display: "flex",
		gap: 0,
		alignItems: "center",
		justifyContent: "flex-start",
	},
	timelineDot: (isActive: boolean) => ({
		bgcolor: isActive ? "#1976D2" : "primary.main",
		boxShadow: isActive ? "0px 0px 0px 4px rgba(25, 118, 210, 0.3)" : "none",
		transition: "background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
	}),
	timelineOppositeContentStop: {
		flex: 1,
		minWidth: "120px",
		maxWidth: "180px",
		overflow: "hidden",
		alignItems: "center",
		justifyContent: "flex-end",
		py: "18px",
	},
	timelineOppositeContentRoute: {
		flex: 1,
		minWidth: "120px",
		maxWidth: "180px",
		overflow: "hidden",
		whiteSpace: "nowrap",
		alignItems: "center",
		justifyContent: "flex-end",
		py: "18px",
	},
	captionLineHeight12: { lineHeight: 1.2 },
	captionLineHeight1: { lineHeight: 1 },

	clickableBox: (isActive: boolean) => {
		const activeBgColor = "rgba(25, 118, 210, 0.08)";
		const hoverBgColor = "rgba(25, 118, 210, 0.12)";
		const borderColor = "#1976D2";

		return {
			p: 1,
			cursor: "pointer",
			borderRadius: 2,
			backgroundColor: isActive ? activeBgColor : "transparent",
			border: isActive ? `1px solid ${borderColor}` : "1px solid transparent",
			boxShadow: isActive ? "0px 2px 4px rgba(0, 0, 0, 0.1)" : "none",

			"&:hover": {
				backgroundColor: isActive ? hoverBgColor : "rgba(0, 0, 0, 0.04)",
				border: isActive ? `1px solid ${borderColor}` : "1px solid transparent",
				boxShadow: isActive ? "0px 4px 8px rgba(0, 0, 0, 0.15)" : "none",
			},
			transition: "background-color 0.2s ease-in-out, border 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
			ml: 1,
			flexGrow: 1,
		};
	},
};


export default MapShipHistoryTimeLine;
